import React, { useMemo } from 'react';
import { isEqual } from 'date-fns';
import { toast } from 'react-toastify';

import { CalendarSchedule } from '../CalendarSchedule';

import { ScheduleItemProps } from '../../../../models/Schedule';
import { DisplayTimes } from '../..';
import { useContextSelector } from 'use-context-selector';
import { ScheduleContext } from '../../../../contexts/ScheduleContext';
import { ModuleContextProps } from '../../../../contexts/ModuleContext';
import { RouterContext } from '../../../../contexts/RouterContext';
import { ProfessionalCard } from '../ProfessionalCard';
import useSpecialistHours from './hooks/useSpecialistHours';
import { ScheduleSpecialist } from '../../../../models/ScheduleSpecialist';
import useTeleorientationSlots from './hooks/useTeleorientationSlots';
import useSGHSlots from './hooks/useSGHSlots';

import * as S from '../../styles';
import DatePickerModal from '../DatePickerModal';
import PatientTime from '../../../../components/PatientTime';
import { CartContext } from '../../../../contexts/CartContext';

export type DisplayDates = { scheduleId: string; dates: Date[] };

type DateSlots = {
  schedules: ScheduleItemProps[];
  schedule: ScheduleItemProps | undefined;
  setSelectedSchedule: React.Dispatch<React.SetStateAction<ScheduleItemProps | undefined>>;
  setHasScheduleSameDateTime: (value: boolean) => void;
  setLoadingSlots: (value: boolean) => void;
  setSelectedDate: (value: Date | null) => void;
  selectedDate: Date | null;
  selectedTime: DisplayTimes | undefined;
  setSelectedTime: (value: DisplayTimes | undefined) => void;
  handleGoBack: () => void;
  scheduleSpecialist: ScheduleSpecialist;
  setSpecialistInterval: (value: number) => void;
} & Pick<ModuleContextProps, 'selectedItemToSchedule'>;

const DateSlots: React.FC<DateSlots> = ({
  schedules,
  schedule,
  setHasScheduleSameDateTime,
  setLoadingSlots,
  setSelectedDate,
  selectedDate,
  selectedTime,
  setSelectedTime,
  selectedItemToSchedule,
  handleGoBack,
  scheduleSpecialist,
  setSelectedSchedule,
  setSpecialistInterval
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [displayDates, setDisplayDates] = React.useState<Date[]>([]);
  const [displayDatesPicker, setDisplayDatesPicker] = React.useState<Date[]>();
  const [productTimes, setProductTimes] = React.useState<DisplayTimes[]>([]);
  const [isProductWithProfessional, setIsProductWithProfessional] = React.useState(false);
  const { findUnitDatesAvailable, eavSlotsData, selectedItem } = useContextSelector(ScheduleContext, state => state);
  const { state: stateParams } = useContextSelector(RouterContext, state => state);
  const { findProduct } = useContextSelector(CartContext, state => state);

  const productRules = useMemo(() => {
    const product = findProduct(selectedItem?.productId ?? '');
    return product?.rules ?? [];
  }, [selectedItem]);

  const { scheduleForRemove, scheduleDateToRemove, scheduleHourToRemove } = stateParams;

  React.useEffect(() => {
    setIsProductWithProfessional(productTimes.some(timeSlot => !!timeSlot.slots.some(slot => !!slot.resourceId)));
  }, [productTimes]);

  const isSameDateTime = (timeSlots: DisplayTimes | undefined, otherTimeSlots: DisplayTimes) => {
    if (timeSlots)
      if (isEqual(timeSlots.time, otherTimeSlots.time)) {
        setHasScheduleSameDateTime(true);
        toast.warning('Já existe um agendamento nesse horário para o mesmo paciente.');
      } else setHasScheduleSameDateTime(false);
  };

  const getSchedulesSamePatient = (timeSlots: DisplayTimes) => {
    schedules.map(schedule => {
      if (schedule.idPatient === schedule?.idPatient)
        if (schedule?.id !== schedule.id) isSameDateTime(schedule.time, timeSlots);
    });
  };

  function handleSelectSlotTime(timeSlots: DisplayTimes) {
    setSelectedTime(timeSlots);
    getSchedulesSamePatient(timeSlots);
  }

  function handleSelectDate(date: Date) {
    setSelectedDate(date);

    if (scheduleSpecialist.id) return onSelectDateSpecialist(date);

    if (schedule?.channel === 'TELEORIENTATION') return onSelectDateTeleorientation(date);

    return onSelectDateSGH(date);
  }

  const { onSelectDateSGH, loadSGHSlotsMonth } = useSGHSlots({
    selectedItemToSchedule: selectedItemToSchedule || '',
    setDisplayDates,
    setDisplayDatesPicker,
    setLoading: setLoadingSlots,
    setSelectedDate,
    addSlotTimes: setProductTimes,
    findUnitDatesAvailable,
    schedule,
    setSelectedTime,
    eavSlotsData,
    scheduleForRemove,
    scheduleDateToRemove,
    scheduleHourToRemove,
    productRules
  });

  const { onSelectDateTeleorientation } = useTeleorientationSlots({
    schedule,
    setDisplayDates,
    setDisplayDatesPicker,
    setLoading: setLoadingSlots,
    addSlotTimes: setProductTimes,
    handleGoBack
  });

  const { loadSpecialistSlotsMonth, onSelectDateSpecialist } = useSpecialistHours({
    selectedItemToSchedule: selectedItemToSchedule || '',
    setDisplayDates,
    setDisplayDatesPicker,
    scheduleSpecialist,
    setLoading: setLoadingSlots,
    addSlotTimes: setProductTimes,
    setSelectedDate,
    setInterval: setSpecialistInterval,
    datePickerMonth: displayDatesPicker?.[0],
    onSelectTime: setSelectedTime
  });

  if (!schedule) return null;

  return (
    <>
      <S.ProfessionalWrapper>
        <ProfessionalCard
          isProductWithProfessional={isProductWithProfessional}
          schedule={schedule}
          selectedTime={selectedTime}
          scheduleSpecialist={scheduleSpecialist}
        />
      </S.ProfessionalWrapper>
      <CalendarSchedule
        displayDates={displayDates}
        selectedDate={selectedDate}
        onSelectDate={handleSelectDate}
        handleClickSeeMore={() => setIsOpen(true)}
      />
      {!!selectedDate && (
        <PatientTime times={productTimes} selectedTime={selectedTime} onSelectedTime={handleSelectSlotTime} />
      )}

      <DatePickerModal
        schedule={schedule}
        selectedDate={selectedDate}
        setSelectedDate={date => {
          if (date) handleSelectDate(date);
        }}
        displayDates={displayDates}
        setDisplayDates={(dates: Date[]) => setDisplayDates(dates)}
        displayDatesPicker={displayDatesPicker}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        selectedItemToSchedule={selectedItemToSchedule}
        getSchedulesDatesByMonth={loadSGHSlotsMonth}
        loadSpecialistSlotsMonth={loadSpecialistSlotsMonth}
      />
    </>
  );
};

export default DateSlots;
